import React from "react";
import { useSignIn } from "@clerk/clerk-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { trackAuthAttempt } from "../lib/analytics";

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { signIn, isLoaded } = useSignIn();

  const authenticateWithOAuth = async (
    strategy: "oauth_github" | "oauth_google",
  ) => {
    if (!isLoaded || !signIn) return;

    try {
      // Track authentication attempt
      const method = strategy === "oauth_github" ? "github" : "google";
      trackAuthAttempt(method);

      // Use signIn.authenticateWithRedirect() for OAuth authentication
      // This method handles both sign-in and sign-up flows automatically
      // If the user doesn't exist, Clerk will create a new account
      // If the user exists, Clerk will sign them in
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: "/app/sso-callback",
        redirectUrlComplete: "/app/",
      });
    } catch (error) {
      console.error(`OAuth authentication with ${strategy} failed:`, error);
    }
  };

  return (
    <div className={cn("flex flex-col gap-8", className)} {...props}>
      {/* Header */}
      <div className="text-center space-y-3 animate-in fade-in-50 slide-in-from-top-4 duration-700">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
          Sign in to Forge
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 font-medium">
          Access your Forge dashboard
        </p>
      </div>

      {/* Form Content */}
      <div className="space-y-4 animate-in fade-in-50 slide-in-from-bottom-4 duration-700 delay-200">
        <Button
          variant="outline"
          className="w-full h-14 text-base font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750 transition-all duration-200 ease-out shadow-sm hover:shadow-md active:scale-[0.98] rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500"
          onClick={() => authenticateWithOAuth("oauth_github")}
          disabled={!isLoaded}
          aria-label="Sign in with GitHub account"
        >
          {!isLoaded ? (
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-3" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="mr-3 h-5 w-5 transition-transform duration-200 group-hover:scale-110"
            >
              <path
                d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"
                fill="currentColor"
              />
            </svg>
          )}
          Sign in with GitHub
        </Button>

        <Button
          variant="outline"
          className="w-full h-14 text-base font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-750 transition-all duration-200 ease-out shadow-sm hover:shadow-md active:scale-[0.98] rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500"
          onClick={() => authenticateWithOAuth("oauth_google")}
          disabled={!isLoaded}
          aria-label="Sign in with Google account"
        >
          {!isLoaded ? (
            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-3" />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="mr-3 h-5 w-5 transition-transform duration-200 group-hover:scale-110"
            >
              <path
                d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                fill="currentColor"
              />
            </svg>
          )}
          Sign in with Google
        </Button>
      </div>
    </div>
  );
}
